import { defaultStorage, type StorageLike } from './annotations.ts'
import type { Friend, FriendStatus } from './types.ts'

/**
 * The local-first friend list.
 *
 * The same shape as the annotation store, and for the same reason: the Firestore
 * document `friendLists/{uid}` is the source of truth, but a user standing in the park
 * with no signal must still see who their friends are and what to call them. Without
 * persistence every friend's note would read `En venn:` on a cold start until the
 * listener reconnected (R14, KTD16), and the friends page would be empty on the one
 * network the app is guaranteed to be used on.
 *
 * The store is read-only from the client's side. Every mutation goes through
 * `/api/friends`, because resolving an address to a uid needs privileged access, and
 * comes back as a fresh snapshot — so there is no local write path to reconcile and no
 * offline queue to keep.
 */

const VERSION = 1
export const FRIENDS_STORAGE_KEY = `oya-annotated:friends:v${VERSION}`

interface Persisted {
  version: number
  friends: Friend[]
}

export interface CreateFriendStoreOptions {
  /** Defaults to `window.localStorage` when available. */
  storage?: StorageLike | null
}

export interface LocalFriendStore {
  /**
   * Monotonic write counter. React binds to this via `useSyncExternalStore`: a number
   * is referentially stable, whereas a freshly-built array from a snapshot getter
   * would re-render forever. Same contract as the annotation store's.
   */
  getVersion(): number
  /** Everyone in the list — friends and requests both ways, for the friends page. */
  friends(): Friend[]
  /**
   * The uids of **accepted** friends only.
   *
   * This is the value that becomes `readers` on every annotation the user writes, so
   * it is a consent boundary rather than a convenience: a pending uid reaching it
   * would grant read access the other person never agreed to (KTD4, R29). It is named
   * for what it guarantees precisely so no call site has to remember to filter, and
   * `friends()` is never the right source for a grant.
   */
  acceptedUids(): string[]
  /** Requests waiting on this user, for the header badge (R31). */
  incomingCount(): number
  /** Replaces the whole list. The only way in; snapshots are always complete. */
  replaceFriends(next: readonly Friend[]): void
  /** Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void
  /** Wipes local state — sign-out, an account switch, and "delete my data". */
  clear(): void
}

function isFriendStatus(value: unknown): value is FriendStatus {
  return value === 'incoming' || value === 'outgoing' || value === 'accepted'
}

/**
 * Validates one stored entry.
 *
 * Fails closed on `status`: an unrecognised value is dropped rather than kept, because
 * every branch downstream asks "is this accepted?" and a record this build cannot read
 * must never be able to answer yes.
 */
function isFriend(value: unknown): value is Friend {
  if (typeof value !== 'object' || value === null) return false
  const f = value as Record<string, unknown>
  return typeof f['uid'] === 'string' && f['uid'] !== '' && isFriendStatus(f['status'])
}

export function createFriendStore(options: CreateFriendStoreOptions = {}): LocalFriendStore {
  const storage = options.storage === undefined ? defaultStorage() : options.storage

  let list: Friend[] = []
  let view: Friend[] | null = null
  const listeners = new Set<() => void>()

  // Load. Anything unreadable is discarded silently: unlike the annotation store there
  // is nothing to warn the user about, because the list is not something they wrote —
  // the next snapshot from the listener restores it in full.
  if (storage) {
    const raw = storage.getItem(FRIENDS_STORAGE_KEY)
    if (raw !== null) {
      try {
        const parsed = JSON.parse(raw) as Persisted
        if (parsed?.version !== VERSION || !Array.isArray(parsed.friends)) {
          throw new Error('ukjent skjemaversjon')
        }
        // Normalise the two nullable fields on the way in rather than trusting what
        // was written: `friendPrefix` calls string methods on both, so anything else
        // reaching them takes the day view down with it.
        list = parsed.friends.filter(isFriend).map((f) => ({
          uid: f.uid,
          email: typeof f.email === 'string' ? f.email : null,
          name: typeof f.name === 'string' ? f.name : null,
          status: f.status,
        }))
      } catch {
        list = []
      }
    }
  }

  let version = 0

  function notify() {
    version += 1
    view = null
    for (const listener of listeners) listener()
  }

  function persist() {
    if (!storage) return
    try {
      const payload: Persisted = { version: VERSION, friends: list }
      storage.setItem(FRIENDS_STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // Safari private mode and full quotas both land here. Keep the in-memory copy so
      // the session still works; the list is re-delivered at the next sign-in anyway.
    }
  }

  return {
    getVersion() {
      return version
    },

    /*
     * Cached per version, like the annotation store's `allAnnotations`. The day view
     * feeds this into a `useMemo` dependency array, and a fresh copy on every call
     * would rebuild the uid lookup on every render rather than on every change.
     */
    friends() {
      return (view ??= [...list])
    },

    acceptedUids() {
      return list.filter((f) => f.status === 'accepted').map((f) => f.uid)
    },

    incomingCount() {
      return list.filter((f) => f.status === 'incoming').length
    },

    replaceFriends(next) {
      list = [...next]
      persist()
      notify()
    },

    subscribe(listener) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },

    clear() {
      list = []
      try {
        storage?.removeItem(FRIENDS_STORAGE_KEY)
      } catch {
        // Nothing to fall back to, and nothing to tell the user: the in-memory list is
        // already gone, which is what the caller asked for.
      }
      notify()
    },
  }
}
